<template>
  <div class="risk-page">
    <section class="hero-card">
      <div class="hero-left">
        <img class="avatar" src="@/assets/img/person-default.png" alt="用户头像">
        <h1>健康风险总览</h1>
      </div>
      <div class="hero-tags">
        <span class="tag tag-blue">{{ patientName }}</span>
        <span class="tag tag-green">{{ patientAge }}</span>
        <span class="tag tag-orange">{{ patientSex }}</span>
      </div>
    </section>

    <section class="top-grid">
      <div class="panel phone-panel">
        <phone-report :reportForm="phoneReportForm" class="phone-report-card"></phone-report>
      </div>

      <div class="right-risk-area">
        <section class="summary-grid">
          <div v-for="item in summaryCards" :key="item.label" class="summary-card">
            <div class="summary-label">{{ item.label }}</div>
            <div :class="['summary-value', item.color]">{{ item.value }}</div>
          </div>
        </section>

        <div class="panel matrix-panel">
          <PanelTitle color="#ffad3d" title="风险矩阵" />
          <div v-if="matrixItems.length" class="risk-matrix">
            <div v-for="item in matrixItems" :key="item.name" class="matrix-row">
              <span>{{ item.name }}</span>
              <span :class="['status-pill', statusClass(item.level)]">{{ statusText(item.level) }}</span>
            </div>
          </div>
          <div v-else class="empty-state">暂无风险矩阵数据</div>
        </div>
      </div>
    </section>

    <section class="middle-grid">
      <div class="panel chronic-panel">
        <PanelTitle color="#7257ff" title="慢病与亚健康状态" />
        <div class="health-grid">
          <div
            v-for="(item, index) in healthStatus"
            :key="item.name"
            :class="['health-item', item.tone]"
          >
            <span class="health-index">{{ index + 1 }}</span>
            <div>
              <strong>{{ item.name }}</strong>
              <p>{{ item.value }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="panel source-panel">
      <PanelTitle color="#ff6b14" title="风险预警与数据来源" />
      <div class="source-table">
        <div class="source-head">
          <span>项目</span>
          <span>预警要点</span>
        </div>
        <div v-for="item in warningSources" :key="item.project" class="source-row">
          <span>{{ item.project }}</span>
          <span>{{ item.warning }}</span>
        </div>
        <div v-if="!warningSources.length" class="source-empty">暂无风险预警与数据来源</div>
      </div>
    </section>
  </div>
</template>

<script>
import PhoneReport from '@/components/phone-report/index2'
import { getSubHealthNew, getChronicNew, getRiskNew } from '@/api/health/manage'

const PanelTitle = {
  functional: true,
  props: {
    color: {
      type: String,
      default: '#2f73ff'
    },
    title: {
      type: String,
      required: true
    }
  },
  render(h, context) {
    return h('div', { class: 'panel-title' }, [
      h('i', { style: { backgroundColor: context.props.color }}),
      h('span', context.props.title)
    ])
  }
}

const DEFAULT_HEALTH = [
  { name: '心血管系统', value: '高血压、房颤、脑出血、冠心病、颈动脉粥样硬化性斑块', tone: 'blue' },
  { name: '呼吸系统', value: '肺结节', tone: 'cyan' },
  { name: '体质指数', value: '正常', tone: 'green' },
  { name: '睡眠状况', value: '睡眠质量一般', tone: 'orange' },
  { name: '阻塞性睡眠呼吸暂停', value: '轻度', tone: 'purple' },
  { name: '心理精神异常', value: '暂缺数据', tone: 'gray' },
  { name: '血脂异常', value: '暂缺数据', tone: 'gray' },
  { name: '糖尿病前期', value: '不适用', tone: 'gray' }
]

export default {
  components: { PanelTitle, PhoneReport },
  data() {
    return {
      reportForm: {
        wxUserId: '',
        jkName: '',
        jkAge: '',
        jkSex: ''
      },
      risks: [],
      subHealthInfo: [],
      chronicDiseaseInfo: []
    }
  },
  computed: {
    patientName() {
      return this.reportForm.jkName || this.reportForm.name || '郭喵喵'
    },
    patientAge() {
      const age = this.reportForm.jkAge || this.reportForm.age || '35'
      return `${age}岁`
    },
    patientSex() {
      const sex = this.reportForm.jkSex || this.reportForm.sex
      if (String(sex) === '1' || sex === '男') return '男性'
      return '女性'
    },
    normalizedRisks() {
      return (this.risks || []).map(item => ({
        name: item.name || item.riskName || '未命名项目',
        level: Number(item.level || item.riskLevel || 0),
        detail: item.detail || item.warning || item.remark || '',
        need: item.need || item.source || item.dataSource || '需补充数据'
      }))
    },
    phoneReportForm() {
      return {
        ...this.reportForm,
        risks: this.risks
      }
    },
    summaryCards() {
      const lack = this.normalizedRisks.filter(item => item.level === 0).length
      const low = this.normalizedRisks.filter(item => item.level === 2).length
      const middle = this.normalizedRisks.filter(item => item.level === 3).length
      const high = this.normalizedRisks.filter(item => item.level >= 4).length

      return [
        { label: '暂缺数据项', value: lack, color: 'slate' },
        { label: '低风险项', value: low, color: 'green' },
        { label: '中风险项', value: middle, color: 'orange' },
        { label: '高风险项', value: high, color: 'red' }
      ]
    },
    matrixItems() {
      return this.normalizedRisks
    },
    healthStatus() {
      const merged = []
      const chronic = this.formatHealthList(this.chronicDiseaseInfo)
      const subHealth = this.formatHealthList(this.subHealthInfo)

      if (chronic.length) merged.push.apply(merged, chronic)
      if (subHealth.length) merged.push.apply(merged, subHealth)

      const tones = ['blue', 'cyan', 'green', 'orange', 'purple', 'gray', 'gray', 'gray']

      return (merged.length ? merged : DEFAULT_HEALTH).map((item, index) => ({
        name: item.name,
        value: item.value || '暂缺数据',
        tone: item.tone || tones[index % tones.length]
      }))
    },
    warningSources() {
      return this.normalizedRisks.map(item => ({
        project: item.name,
        warning: item.detail || this.warningByLevel(item.level)
      }))
    }
  },
  created() {
    this.reportForm.wxUserId = this.$route.query.wxUserId || ''
    this.reportForm.jkName = this.$route.query.jkName || ''
    this.reportForm.jkAge = this.$route.query.jkAge || ''
    this.reportForm.jkSex = this.$route.query.jkSex || ''
    this.loadRisk()
    this.loadSubHealth()
    this.loadChronic()
  },
  methods: {
    loadRisk() {
      if (!this.reportForm.wxUserId) {
        return
      }

      getRiskNew(this.reportForm.wxUserId).then(res => {
        if (!res || !res.success || !res.data) return
        const userJk = res.data.userJk || {}
        this.reportForm = {
          ...this.reportForm,
          wxUserId: userJk.wxUserId || this.reportForm.wxUserId,
          jkName: userJk.name || this.reportForm.jkName,
          jkAge: userJk.age || userJk.jkAge || this.reportForm.jkAge,
          jkSex: userJk.sex || this.reportForm.jkSex
        }
        this.risks = res.data.risks || []
      })
    },
    loadSubHealth() {
      if (!this.reportForm.wxUserId) return
      getSubHealthNew(this.reportForm.wxUserId).then(res => {
        this.subHealthInfo = Array.isArray(res && res.data) ? res.data : []
      })
    },
    loadChronic() {
      if (!this.reportForm.wxUserId) return
      getChronicNew(this.reportForm.wxUserId).then(res => {
        this.chronicDiseaseInfo = Array.isArray(res && res.data) ? res.data : []
      })
    },
    formatHealthList(list) {
      if (!Array.isArray(list)) return []
      return list.map(item => ({
        name: item.name || item.label || item.title || '健康状态',
        value: item.value || item.result || item.status || '暂缺数据'
      }))
    },
    statusText(level) {
      if (Number(level) >= 4) return '高风险'
      if (Number(level) === 3) return '中风险'
      if (Number(level) === 2) return '低风险'
      if (Number(level) === 1) return '轻度异常'
      return '需补充数据'
    },
    statusClass(level) {
      if (Number(level) >= 4) return 'high'
      if (Number(level) === 3) return 'middle'
      if (Number(level) === 2) return 'low'
      if (Number(level) === 1) return 'mild'
      return 'empty'
    },
    warningByLevel(level) {
      if (Number(level) >= 3) return '存在风险提示，建议尽快复核并干预'
      if (Number(level) === 2) return '评估为低风险，建议保持定期监测'
      return '关键数据缺失，暂无法判定'
    }
  }
}
</script>

<style scoped>
.risk-page {
  min-height: 100vh;
  padding: 34px 40px 52px;
  background: #f1f6fb;
  color: #142239;
  font-family: "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
}

.hero-card,
.panel,
.summary-card {
  background: #fff;
  border: 1px solid #dde8f3;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(36, 63, 97, 0.03);
}

.hero-card {
  min-height: 104px;
  margin-bottom: 24px;
  padding: 16px 56px 16px 22px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.hero-left {
  display: flex;
  align-items: center;
  gap: 24px;
}

.avatar {
  width: 64px;
  height: 64px;
  border-radius: 14px;
  object-fit: cover;
}

.hero-left h1 {
  margin: 0;
  font-size: 28px;
  line-height: 1;
  font-weight: 800;
  color: #132239;
}

.hero-tags {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-top: 6px;
}

.tag {
  min-width: 112px;
  height: 30px;
  padding: 0 18px;
  border-radius: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}

.tag-blue {
  background: #edf4ff;
  color: #2e6dff;
}

.tag-green {
  background: #eef9f4;
  color: #10a56b;
}

.tag-orange {
  background: #fff1ea;
  color: #e96741;
}

.summary-grid {
  width: 100%;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.summary-card {
  height: 70px;
  padding: 14px 18px;
}

.summary-label {
  color: #8290a5;
  font-size: 14px;
  line-height: 18px;
}

.summary-value {
  margin-top: 4px;
  font-size: 25px;
  line-height: 1;
  font-weight: 800;
}

.summary-value.green {
  color: #1ec79d;
}

.summary-value.slate {
  color: #637083;
}

.summary-value.orange {
  color: #ff8a00;
}

.summary-value.purple {
  color: #705bff;
}

.summary-value.red {
  color: #df493e;
}

.top-grid {
  display: grid;
  grid-template-columns: 430px 1fr;
  gap: 24px;
  align-items: start;
}

.right-risk-area {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.middle-grid {
  margin-top: 36px;
  display: grid;
  grid-template-columns: 1fr;
  align-items: start;
}

.panel {
  padding: 18px 24px 24px;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  font-size: 22px;
  line-height: 28px;
  font-weight: 800;
  color: #142239;
}

.panel-title i {
  width: 7px;
  height: 32px;
  border-radius: 5px;
  display: inline-block;
}

.matrix-panel {
  min-height: 350px;
}

.phone-panel {
  padding: 22px 24px;
  display: flex;
  justify-content: center;
  min-height: 764px;
}

.phone-report-card {
  flex: 0 0 auto;
}

.risk-matrix {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 72px;
}

.empty-state,
.source-empty {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9aa7b8;
  font-size: 14px;
  background: #f8fafc;
  border: 1px dashed #dbe5f0;
  border-radius: 10px;
}

.matrix-row {
  height: 44px;
  padding: 0 14px 0 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  background: #f7f9fc;
  border: 1px solid #edf2f7;
  border-radius: 10px;
  color: #26334a;
  font-size: 14px;
}

.status-pill {
  min-width: 74px;
  height: 24px;
  padding: 0 12px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.status-pill.low {
  color: #18aa72;
  background: #dcf8eb;
}

.status-pill.empty {
  color: #6b7788;
  background: #eef2f7;
}

.status-pill.mild,
.status-pill.middle {
  color: #f08400;
  background: #fff0d7;
}

.status-pill.high {
  color: #df493e;
  background: #ffe7e4;
}

.chronic-panel {
  min-height: 285px;
}

.health-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  gap: 12px 34px;
}

.health-item {
  height: 52px;
  padding: 8px 18px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px solid #edf2f7;
}

.health-item.blue {
  background: #eef3ff;
}

.health-item.cyan {
  background: #eafcfc;
}

.health-item.green {
  background: #edfbf2;
}

.health-item.orange {
  background: #fff5ea;
}

.health-item.purple {
  background: #f3efff;
}

.health-item.gray {
  background: #f8fafc;
}

.health-index {
  width: 34px;
  height: 34px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: #2c91d8;
  font-size: 16px;
  font-weight: 800;
  flex: 0 0 34px;
}

.health-item strong {
  display: block;
  color: #192841;
  font-size: 15px;
  line-height: 18px;
}

.health-item p {
  margin: 2px 0 0;
  color: #718197;
  font-size: 12px;
  line-height: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.source-panel {
  margin-top: 36px;
  padding-bottom: 26px;
}

.source-table {
  font-size: 13px;
  color: #617087;
}

.source-head,
.source-row {
  display: grid;
  grid-template-columns: 210px 1fr;
  align-items: center;
  min-height: 28px;
  padding: 0 20px;
}

.source-head {
  height: 36px;
  margin-bottom: 8px;
  border-radius: 10px;
  background: #fff7ef;
  border: 1px solid #ffd9b5;
  color: #b24d19;
  font-weight: 800;
}

.source-row {
  border-radius: 8px;
}

.source-row:nth-child(odd) {
  background: #f8fbff;
}

.source-row span:first-child {
  color: #223047;
  font-weight: 700;
}

.source-empty {
  min-height: 72px;
  margin-top: 8px;
}

@media (max-width: 1400px) {
  .top-grid,
  .middle-grid {
    grid-template-columns: 1fr;
  }

  .summary-grid {
    width: 100%;
  }

  .source-head,
  .source-row {
    grid-template-columns: 180px 1fr;
  }
}

@media (max-width: 768px) {
  .risk-page {
    padding: 18px;
  }

  .hero-card {
    padding: 18px;
    flex-direction: column;
    gap: 22px;
  }

  .hero-left h1 {
    font-size: 26px;
  }

  .hero-tags,
  .summary-grid,
  .risk-matrix,
  .health-grid {
    grid-template-columns: 1fr;
    display: grid;
    width: 100%;
  }

  .middle-grid,
  .top-grid {
    gap: 18px;
  }

  .source-table {
    overflow-x: auto;
  }

  .source-head,
  .source-row {
    min-width: 560px;
  }

  .phone-panel {
    min-height: auto;
    overflow-x: auto;
  }
}
</style>
